import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { ExactOnlineConnection } from 'app/shared/models/exact-online-connection.model';
import { SettingsService } from 'app/shared/services/settings.service';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { SettingsBaseComponent } from '../settings-base/settings-base.component';

/** One headed block of settings on the Exact Online page. */
interface ExactOnlineSettingGroup {
  titleKey: string;
  descriptionKey: string;
  icon: string;
  settingNames: string[];
}

/** One field, resolved once so the template only reads properties. */
interface ExactOnlineSettingField {
  name: string;
  index: number;
  settingTypeCode: number;
  displayName: string;
  placeholder: string;
  isReadOnly: boolean;
  control: AbstractControl;
}

/** What a settings check had to say about one setting, without it being a problem. */
interface ExactOnlineAdvice {
  Setting: string;
  Message: string;
  SuggestedValue: string;
}

/** A group together with the fields it renders. */
interface ExactOnlineRenderedGroup {
  group: ExactOnlineSettingGroup;
  fields: ExactOnlineSettingField[];
}

@Component({
  selector: 'app-exact-online-settings',
  templateUrl: './exact-online-settings.component.html',
  styleUrls: ['./exact-online-settings.component.scss']
})
export class ExactOnlineSettingsComponent extends SettingsBaseComponent implements OnInit, OnDestroy {

  /** The settings the API client writes itself, shown for insight but never submitted. */
  static readonly READ_ONLY_SETTINGS = [
    'ExactOnlineRefreshToken',
    'ExactOnlineRefreshTokenExpiresAt'
  ];

  /**
   * Settings that are never rendered. The refresh token is the server's Exact Online identity and
   * the API does not send its value, so a field for it would show an empty box that cannot be
   * filled in. Whether there is a token, and until when, is what the connection state says.
   */
  static readonly HIDDEN_SETTINGS = [
    'ExactOnlineRefreshToken'
  ];

  /**
   * The settings that go into the authorization url. Logging in with an unsaved edit to any of them
   * would send the browser to Exact Online with one value while the code exchange afterwards uses
   * the stored one, which fails at the redirect with nothing useful to read.
   */
  static readonly LOGIN_SETTINGS = [
    'ExactOnlineBaseUrl',
    'ExactOnlineClientId',
    'ExactOnlineClientSecret',
    'ExactOnlineRedirectUri'
  ];

  /**
   * An example value per setting, or the value that applies when it is left empty. Shown as the
   * placeholder, which is why none of these repeats the field's label.
   */
  static readonly PLACEHOLDERS: { [settingName: string]: string } = {
    'ExactOnlineBaseUrl': 'https://start.exactonline.nl',
    'ExactOnlineClientId': '1a2b3c4d-5e6f-7a8b-9c0d-1e2f3a4b5c6d',
    'ExactOnlineClientSecret': 'aBcDeFgHiJkL',
    'ExactOnlineRedirectUri': 'https://api.ais.nidaros.nl/exact-online/callback',
    'ExactOnlineDivision': '3807112',
    'ExactOnlineCapabilityItemId': 'aaaaaaaa-bbbb-cccc-dddd-eeeeeeeeeeee',
    'ExactOnlineInvoiceItemCode': 'UPDTRANSACTION',
    'ExactOnlineVatCode': '01',
    'ExactOnlinePaymentDays': '30'
  };

  /** The page in reading order. */
  private static readonly GROUPS: ExactOnlineSettingGroup[] = [
    {
      titleKey: 'exactOnlineSettings.connectionHeader',
      descriptionKey: 'exactOnlineSettings.connectionText',
      icon: 'icon-key',
      settingNames: ExactOnlineSettingsComponent.LOGIN_SETTINGS
    },
    {
      titleKey: 'exactOnlineSettings.administrationHeader',
      descriptionKey: 'exactOnlineSettings.administrationText',
      icon: 'ft-book',
      settingNames: [
        'ExactOnlineDivision',
        'ExactOnlineCapabilityItemId',
        'ExactOnlineInvoiceItemCode',
        'ExactOnlineSalesJournalCode',
        'ExactOnlineVatCode',
        'ExactOnlinePaymentDays'
      ]
    },
    {
      titleKey: 'exactOnlineSettings.tokenHeader',
      descriptionKey: 'exactOnlineSettings.tokenText',
      icon: 'ft-shield',
      settingNames: ExactOnlineSettingsComponent.READ_ONLY_SETTINGS
    }
  ];

  /** Where a finding lands when the response carries no per-setting structure. */
  static readonly GENERAL_PROBLEM_KEY = 'ExactOnline';

  /** The key the token group is recognised by, so the connection state renders under it. */
  readonly tokenGroupKey = 'exactOnlineSettings.tokenHeader';

  /**
   * Built once, when the settings arrive, rather than computed per render. A getter here would hand
   * *ngFor a new array every change-detection cycle, which rebuilds every field's DOM on every
   * click and keystroke: the inputs lose focus while you type, and anything watching the form (a
   * browser password manager, for one) re-scans each rebuilt subtree.
   */
  renderedGroups: ExactOnlineRenderedGroup[] = [];

  connection: ExactOnlineConnection = { IsConnected: false, RefreshTokenExpiresAt: null };

  checkedSavedMessage: string;
  checkedUnsavedMessage: string;

  /**
   * What the last check against Exact Online reported, keyed by the setting each finding is about.
   * Kept on the page rather than shown as a toast, so a finding sits beside the field it concerns.
   * A field's findings are dropped the moment that field is edited, because they then describe a
   * value that no longer applies.
   */
  validationProblems: { [settingName: string]: string[] } = {};

  /**
   * What the last check had to say about settings that are not wrong, keyed by setting. Mostly the
   * value that applies while a field is left empty, which is otherwise only visible as a greyed out
   * placeholder that says nothing about whether the administration accepts it.
   */
  advice: { [settingName: string]: ExactOnlineAdvice[] } = {};

  private formChanges: Subscription;
  private lastFormValue: { [settingName: string]: string } = {};

  constructor(protected formBuilder: FormBuilder,
              protected settingsService: SettingsService,
              protected toastrService: ToastrService,
              translateService: TranslateService) {
    super(formBuilder, settingsService, toastrService, translateService);
    this.sourceUrl = '/setting/exact-online';
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.getConnection();

    this.translateService.get(['exactOnlineSettings.checkedSavedMessage', 'exactOnlineSettings.checkedUnsavedMessage'])
      .subscribe(translations => {
        this.checkedSavedMessage = translations['exactOnlineSettings.checkedSavedMessage'];
        this.checkedUnsavedMessage = translations['exactOnlineSettings.checkedUnsavedMessage'];
      });
  }

  ngOnDestroy(): void {
    if (this.formChanges) {
      this.formChanges.unsubscribe();
    }
  }

  addSettingsToForm() {
    super.addSettingsToForm();
    this.buildRenderedGroups();

    this.lastFormValue = this.currentFormValue();

    if (!this.formChanges) {
      this.formChanges = this.dynamicForm.valueChanges
        .subscribe(() => this.clearValidationForChangedFields());
    }
  }

  /** Drops every finding and every piece of advice, for a fresh check. */
  clearValidation(): void {
    this.validationProblems = {};
    this.advice = {};
  }

  /** The findings about one setting, if the last check reported any. */
  problemsFor(settingName: string): string[] {
    return this.validationProblems[settingName] || [];
  }

  /** The advice about one setting, if the last check had any. */
  adviceFor(settingName: string): ExactOnlineAdvice[] {
    return this.advice[settingName] || [];
  }

  /** Puts a suggested value in its field, so it can be saved rather than copied by hand. */
  applyAdvice(settingName: string, suggestedValue: string): void {
    const control = this.controlOf(settingName);

    if (!control) {
      return;
    }

    control.patchValue({ value: suggestedValue });
    control.markAsDirty();
    this.dynamicForm.markAsDirty();
  }

  /** Findings that belong to no field on this page, such as access to the sales invoices. */
  get generalProblems(): string[] {
    const named = this.formSettings.controls.map(control => control.value.name);

    return Object.keys(this.validationProblems)
      .filter(key => named.indexOf(key) === -1)
      .reduce((all, key) => all.concat(this.validationProblems[key]), [] as string[]);
  }

  /** Whether the last check reported anything at all. */
  get hasValidationProblems(): boolean {
    return Object.keys(this.validationProblems).length > 0;
  }

  /**
   * Drops the findings for any setting whose value has changed since the last check, leaving the
   * rest in place. Editing the VAT code says nothing about the sales journal.
   */
  private clearValidationForChangedFields(): void {
    const current = this.currentFormValue();

    Object.keys(current).forEach(name => {
      if (current[name] === this.lastFormValue[name]) {
        return;
      }

      delete this.validationProblems[name];
      delete this.advice[name];
    });

    this.lastFormValue = current;
  }

  private currentFormValue(): { [settingName: string]: string } {
    return this.formSettings.value.reduce((values, setting) => {
      values[setting.name] = setting.value;
      return values;
    }, {});
  }

  /** Identity for *ngFor, so a rebuild of the view model does not rebuild unchanged DOM. */
  trackGroup(index: number, entry: ExactOnlineRenderedGroup): string {
    return entry.group.titleKey;
  }

  /** Identity for *ngFor, as above. */
  trackField(index: number, field: ExactOnlineSettingField): string {
    return field.name;
  }

  /** Whether this setting is written by the API client rather than by a person. */
  isReadOnly(settingName: string): boolean {
    return ExactOnlineSettingsComponent.READ_ONLY_SETTINGS.indexOf(settingName) !== -1;
  }

  /**
   * The login button needs a base url to send the browser to, and needs every value it puts on the
   * authorization url to be the value the backend will exchange the code with. So: no empty base
   * url, and nothing edited since the last save.
   */
  get isLoginDisabled(): boolean {
    const baseUrl = this.valueOf('ExactOnlineBaseUrl');
    if (!baseUrl || !baseUrl.trim()) {
      return true;
    }

    return ExactOnlineSettingsComponent.LOGIN_SETTINGS.some(name => {
      const control = this.controlOf(name);
      return control != null && control.dirty;
    });
  }

  /**
   * Checks the administration codes against Exact Online. Only useful once connected, so the button
   * is hidden until then. The posted values are the ones on the form, so the page can be checked
   * before it is saved.
   */
  test(): void {
    this.clearValidation();

    // Which values were checked is the thing to be precise about: the check reads the form, the
    // invoicing job reads what is stored. A page with unsaved edits passing its check says nothing
    // about what the job will do.
    const message = this.dynamicForm.dirty
      ? this.checkedUnsavedMessage
      : this.checkedSavedMessage;

    this.settingsService.testSettingsQuietly(
      this.sourceUrl + '/test',
      this.formSettings.value.filter(setting => !this.isReadOnly(setting.name)),
      (adviceGiven: ExactOnlineAdvice[]) => {
        this.clearValidation();
        this.advice = ExactOnlineSettingsComponent.bySetting(adviceGiven);
      },
      message,
      (error: HttpErrorResponse) => this.showValidation(error));
  }

  /**
   * Reads the findings out of the response.
   *
   * The API answers a failed check with ValidationProblemDetails, keyed by the setting each finding
   * is about, which is the shape ModelState validation already returns from this API. Anything else
   * (a network failure, a 500) has no such structure, so it is shown as the one message it is.
   */
  private showValidation(error: HttpErrorResponse): void {
    const responseBody: any = error ? error.error : null;

    // Advice rides along on a failed check as a problem-details extension member, because the
    // settings worth advising on are usually the ones that made the check fail.
    this.advice = ExactOnlineSettingsComponent.bySetting(responseBody ? responseBody.advice : null);

    if (responseBody && responseBody.errors) {
      this.validationProblems = responseBody.errors;
      this.lastFormValue = this.currentFormValue();
      return;
    }

    this.validationProblems = { [ExactOnlineSettingsComponent.GENERAL_PROBLEM_KEY]: [this.readErrorMessage(error)] };
    this.lastFormValue = this.currentFormValue();
  }

  /** The server's own message, in the order the dashboard's shared error handling looks for it. */
  private readErrorMessage(error: HttpErrorResponse): string {
    const responseBody: any = error ? error.error : null;

    if (responseBody && responseBody.Message) {
      return responseBody.Message;
    }

    if (responseBody && responseBody.ExceptionMessage) {
      return responseBody.ExceptionMessage;
    }

    if (typeof responseBody === 'string' && responseBody) {
      return responseBody;
    }

    return (error && error.message) ? error.message : 'Unknown error';
  }

  /** Sends the browser to the Exact Online login and consent screen. */
  login(): void {
    this.settingsService.getObject<{ Url: string }>(
      this.sourceUrl + '/authorization-url',
      null,
      authorization => {
        if (authorization && authorization.Url) {
          window.location.href = authorization.Url;
        }
      });
  }

  onSubmit() {
    const writableSettings = this.formSettings.value
      .filter(setting => !this.isReadOnly(setting.name));

    return this.settingsService.saveSettings(
      this.sourceUrl,
      writableSettings,
      () => {
        // The saved values are the ones the backend will use, so the login button is live again.
        this.dynamicForm.markAsPristine();
        this.getConnection();
      },
      this.savedSuccessfullyMessage);
  }

  resetForm() {
    this.renderedGroups = [];
    super.resetForm();
    this.getConnection();
  }

  /**
   * Resolves each group's fields to their position in the form array. A setting no group claims
   * lands in a trailing group, so adding one to the backend cannot make it vanish from the page.
   */
  private buildRenderedGroups(): void {
    const claimed: string[] = [];

    const rendered = ExactOnlineSettingsComponent.GROUPS.map(group => {
      const fields = group.settingNames
        .map(name => this.fieldFor(name))
        .filter(field => field != null);

      fields.forEach(field => claimed.push(field.name));

      return { group: group, fields: fields };
    }).filter(entry => entry.fields.length > 0);

    const unclaimed = this.formSettings.controls
      .map(control => this.fieldFor(control.value.name))
      .filter(field => field != null && claimed.indexOf(field.name) === -1);

    if (unclaimed.length) {
      rendered.push({
        group: {
          titleKey: 'exactOnlineSettings.otherHeader',
          descriptionKey: 'exactOnlineSettings.otherText',
          icon: 'ft-settings',
          settingNames: unclaimed.map(field => field.name)
        },
        fields: unclaimed
      });
    }

    this.renderedGroups = rendered;
  }

  private fieldFor(settingName: string): ExactOnlineSettingField {
    if (ExactOnlineSettingsComponent.HIDDEN_SETTINGS.indexOf(settingName) !== -1) {
      return null;
    }

    const index = this.formSettings.controls
      .findIndex(control => control.value.name === settingName);

    if (index === -1) {
      return null;
    }

    const control = this.formSettings.at(index);

    return {
      name: settingName,
      index: index,
      settingTypeCode: control.value.settingTypeCode,
      displayName: control.value.displayName,
      placeholder: ExactOnlineSettingsComponent.PLACEHOLDERS[settingName] || '',
      isReadOnly: this.isReadOnly(settingName),
      control: control
    };
  }

  private getConnection(): void {
    this.settingsService.getObject<ExactOnlineConnection>(
      this.sourceUrl + '/connection',
      null,
      connection => {
        if (connection) {
          this.connection = connection;
        }
      });
  }

  private controlOf(name: string): AbstractControl {
    return this.formSettings.controls
      .find(control => (control as FormGroup).value.name === name);
  }

  private valueOf(name: string): string {
    const control = this.controlOf(name);
    return control ? control.value.value : null;
  }

  /** Groups the advice by the setting it is about, so a field can ask for its own. */
  private static bySetting(advice: ExactOnlineAdvice[]): { [settingName: string]: ExactOnlineAdvice[] } {
    if (!advice || !advice.length) {
      return {};
    }

    return advice.reduce((all, entry) => {
      (all[entry.Setting] = all[entry.Setting] || []).push(entry);
      return all;
    }, {} as { [settingName: string]: ExactOnlineAdvice[] });
  }
}
